/**
 * DemoActivityFeed
 * ----------------
 * Tiny rotating "live activity" ticker that only renders for users in the
 * SparkleClean demo tenant. Entirely client-side — never writes to the DB,
 * never calls the backend.
 *
 * Updates every 7 seconds with a fresh random event; timestamps drift
 * forward so the list keeps feeling "alive".
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Activity, Clock, AlertTriangle, RefreshCcw, CheckCircle2, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isDemoUser } from '../lib/demo';

const ACTIVITY_TEMPLATES = [
  { icon: Clock,        tone: 'emerald', text: 'Jessica clocked in at Toronto Downtown HQ' },
  { icon: Clock,        tone: 'emerald', text: 'Aisha Khan clocked out (8.2h)' },
  { icon: CheckCircle2, tone: 'emerald', text: 'Payroll approved for Toronto Downtown HQ' },
  { icon: RefreshCcw,   tone: 'sky',     text: 'Shift swap accepted: Michael ↔ Sarah' },
  { icon: AlertTriangle,tone: 'amber',   text: 'Eva detected overtime risk at Etobicoke West Branch' },
  { icon: Sparkles,     tone: 'violet',  text: 'Weekly labor cost report generated' },
  { icon: Clock,        tone: 'emerald', text: 'Late shift started at Mississauga Service Hub' },
  { icon: CheckCircle2, tone: 'emerald', text: 'Timesheet approved for Maria Costa' },
  { icon: AlertTriangle,tone: 'amber',   text: 'Missed break flagged — Diego H. (Etobicoke)' },
  { icon: Sparkles,     tone: 'violet',  text: 'KPI score 94 logged for supervisor Sophia T.' },
  { icon: RefreshCcw,   tone: 'sky',     text: 'New schedule published — Mississauga (Week 21)' },
  { icon: CheckCircle2, tone: 'emerald', text: 'Cash advance repayment deducted: Tamara S.' },
  { icon: Clock,        tone: 'emerald', text: 'Felix Schmidt clocked in (morning office route)' },
  { icon: Sparkles,     tone: 'violet',  text: 'Eva suggests reassigning Liam to Toronto on Friday' },
  { icon: AlertTriangle,tone: 'amber',   text: '2 staff near overtime threshold this week' },
];

const TONE_CLASSES = {
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber:   'bg-amber-50  text-amber-700  border-amber-200',
  sky:     'bg-sky-50    text-sky-700    border-sky-200',
  violet:  'bg-violet-50 text-violet-700 border-violet-200',
};

function timeAgo(secondsAgo) {
  if (secondsAgo < 60) return `${Math.max(1, Math.floor(secondsAgo))} sec ago`;
  const m = Math.floor(secondsAgo / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return `${h} hr ago`;
}

function pickInitial() {
  // Start with 5 distinct templates with varied timestamps (1m–22m).
  const shuffled = [...ACTIVITY_TEMPLATES].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, 5).map((t, i) => ({
    id: `${Date.now()}-${i}`,
    tpl: t,
    ts: Date.now() - (60 + i * 240) * 1000, // 1m, 5m, 9m, 13m, 17m
  }));
}

export default function DemoActivityFeed() {
  const { user } = useAuth();
  const isDemo = useMemo(() => isDemoUser(user), [user]);

  const [items, setItems] = useState(pickInitial);
  const [tick, setTick] = useState(0);

  // Refresh "time ago" strings every 30s
  useEffect(() => {
    if (!isDemo) return;
    const id = setInterval(() => setTick(t => t + 1), 30 * 1000);
    return () => clearInterval(id);
  }, [isDemo]);

  // Add a new event every 7s (push to top, drop the oldest)
  useEffect(() => {
    if (!isDemo) return;
    const id = setInterval(() => {
      setItems(prev => {
        const candidates = ACTIVITY_TEMPLATES.filter(t => !prev.find(p => p.tpl.text === t.text));
        const tpl = candidates.length
          ? candidates[Math.floor(Math.random() * candidates.length)]
          : ACTIVITY_TEMPLATES[Math.floor(Math.random() * ACTIVITY_TEMPLATES.length)];
        const next = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          tpl,
          ts: Date.now(),
        };
        return [next, ...prev].slice(0, 6);
      });
    }, 7000);
    return () => clearInterval(id);
  }, [isDemo]);

  if (!isDemo) return null;

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"
      data-testid="demo-activity-feed"
      aria-label="Live activity feed"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-emerald-500" />
          <h3 className="font-semibold text-slate-900 text-sm">Live Activity</h3>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          Live
        </span>
      </div>

      <ul className="space-y-2.5">
        {items.map(item => {
          const Icon = item.tpl.icon;
          const tone = TONE_CLASSES[item.tpl.tone] || TONE_CLASSES.emerald;
          const seconds = Math.max(1, Math.floor((Date.now() - item.ts) / 1000));
          // 'tick' triggers re-render so timeAgo stays fresh; reference it once
          void tick;
          return (
            <li
              key={item.id}
              className="flex items-start gap-2.5 text-sm transition-opacity duration-500"
              data-testid="demo-activity-item"
            >
              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full border ${tone} flex-shrink-0`}>
                <Icon size={13} />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-slate-700 leading-snug">{item.tpl.text}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{timeAgo(seconds)}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
